/**
 * `text/event-stream`, read as the HTML standard reads it.
 *
 * The decoder is pure: feed it text in whatever pieces the network hands
 * over (a line, or an event, may be cut anywhere, even between CR and LF)
 * and it returns the events each piece completed. `data:` lines are joined
 * with newlines; a line starting with `:` is a comment (the server's `: ka`
 * keepalive) and dispatches nothing; an event with no data is dropped, and so
 * is a half-sent event when the stream ends.
 */

export interface SseMessage {
  /** `message` when the event named none. */
  readonly event: string;
  readonly data: string;
  /** The last id the stream set, if any. */
  readonly id: string | null;
  readonly retry: number | null;
}

export class SseDecoder {
  private buffer = '';
  /** A CR ended the last chunk; an LF opening the next one belongs to it. */
  private pendingCr = false;
  private data: string[] = [];
  private event = '';
  private lastId: string | null = null;
  private retry: number | null = null;
  /** Comment lines seen (keepalives), for whoever wants to know the stream is alive. */
  comments = 0;

  /** Feed decoded text; get the events it completed. */
  push(text: string): SseMessage[] {
    let chunk = text;
    if (this.pendingCr && chunk.startsWith('\n')) chunk = chunk.slice(1);
    this.pendingCr = false;
    this.buffer += chunk;
    const out: SseMessage[] = [];
    let start = 0;
    for (let i = 0; i < this.buffer.length; i++) {
      const c = this.buffer[i];
      if (c !== '\n' && c !== '\r') continue;
      const line = this.buffer.slice(start, i);
      if (c === '\r') {
        if (i + 1 < this.buffer.length) {
          if (this.buffer[i + 1] === '\n') i++;
        } else {
          this.pendingCr = true;
        }
      }
      start = i + 1;
      const done = this.line(line);
      if (done) out.push(done);
    }
    this.buffer = this.buffer.slice(start);
    return out;
  }

  /** The stream ended: an event without its blank line is not dispatched. */
  end(): SseMessage[] {
    this.buffer = '';
    this.data = [];
    this.event = '';
    this.pendingCr = false;
    return [];
  }

  private line(line: string): SseMessage | null {
    if (line === '') {
      if (this.data.length === 0) { this.event = ''; return null; }
      const message: SseMessage = { event: this.event || 'message', data: this.data.join('\n'), id: this.lastId, retry: this.retry };
      this.data = [];
      this.event = '';
      return message;
    }
    if (line.startsWith(':')) { this.comments++; return null; }
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'event': this.event = value; break;
      case 'data': this.data.push(value); break;
      case 'id': if (!value.includes('\0')) this.lastId = value; break;
      case 'retry': if (/^\d+$/.test(value)) this.retry = Number(value); break;
      default: break;
    }
    return null;
  }
}

/**
 * The events of a byte stream, as they complete. Stops quietly when `signal`
 * aborts, or when the reader stops asking; either way the stream is
 * cancelled, so the server sees the reader go.
 */
export async function* readSse(stream: ReadableStream<Uint8Array>, signal?: AbortSignal): AsyncGenerator<SseMessage> {
  const reader = stream.getReader();
  const text = new TextDecoder('utf-8');
  const decoder = new SseDecoder();
  let ended = false;
  const cancel = () => { reader.cancel().catch(() => undefined); };
  if (signal?.aborted) { cancel(); return; }
  signal?.addEventListener('abort', cancel, { once: true });
  try {
    while (!signal?.aborted) {
      let chunk: Awaited<ReturnType<typeof reader.read>>;
      try {
        chunk = await reader.read();
      } catch {
        ended = true;
        return;
      }
      if (chunk.done) {
        ended = true;
        for (const m of decoder.push(text.decode())) yield m;
        decoder.end();
        return;
      }
      for (const m of decoder.push(text.decode(chunk.value, { stream: true }))) {
        if (signal?.aborted) return;
        yield m;
      }
    }
  } finally {
    signal?.removeEventListener('abort', cancel);
    if (!ended) cancel();
    try { reader.releaseLock(); } catch { /* already released by cancel */ }
  }
}
